import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { EffectivePagination, LocalPagination } from "@/domain/types";

const DEFAULT_ITEMS_PER_PAGE = 10;

/** localStorage key, shared with app/initPaginationStore.ts (legacy migration). */
export const PAGINATION_STORAGE_KEY = "pagination_items_per_page";

interface PaginationState {
  itemsPerPage: number;
  setItemsPerPage: (value: number) => void;
  reset: () => void;
}

/**
 * Persisted via zustand's default localStorage storage. Hydration is skipped
 * here and triggered from app/initPaginationStore.ts, after the legacy value
 * format (a bare number written by the old PaginationStorageService) has been
 * migrated to the persist envelope.
 */
export const usePaginationStore = create<PaginationState>()(
  devtools(
    persist(
      (set) => ({
        itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
        setItemsPerPage: (value: number) => set({ itemsPerPage: value }),
        reset: () => set({ itemsPerPage: DEFAULT_ITEMS_PER_PAGE }),
      }),
      {
        name: PAGINATION_STORAGE_KEY,
        skipHydration: true,
        partialize: (state) => ({ itemsPerPage: state.itemsPerPage }),
      },
    ),
    { name: "PaginationStore" },
  ),
);

/**
 * Creates effective pagination by combining local page state with global items per page.
 */
export const createEffectivePagination = (
  localPagination: LocalPagination,
  globalItemsPerPage: number,
): EffectivePagination => {
  const page = Math.max(1, localPagination.currentPage);
  const perPage = Math.max(1, globalItemsPerPage);

  return {
    currentPage: page,
    itemsPerPage: perPage,
    offset: (page - 1) * perPage,
    limit: perPage,
  };
};
